import TeamMemberService from 'services/TeamMemberService';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchAndRepairDashboardConfig } from '__components/DashboardGrid/utils';
import { createPortal } from 'react-dom';
import { useCallback, useContext, useMemo } from 'react';
import { IconDropdown } from 'ts-components/IconDropdown';
import { useCalendarOptions } from '@kizenapps/engine/react';
import { Icon } from '@kizen/kds/Icon';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTooltip } from '__components/Kizen/Tooltip';
import { ICON_BUTTON_DELAY } from 'components/Kizen/Tooltip';
import {
  dashboardPageConfigKey,
  dashboardUserConfigKey,
  getExternalCalendarsFlagFromDashlet,
} from '__pages/Dashboard/util';
import { encodeOptionValue } from './helpers';
import { PluginContext } from 'ts-components/Plugins/context';

export const CalendarDropdown: any = ({
  dashlet,
  errorServices,
  isMobile,
  isLoading,
}: {
  dashlet: any;
  errorServices: string[];
  isMobile?: boolean;
  isLoading?: boolean;
}) => {
  const target = document.querySelector(
    `.dashlet-calendar-renderer-${dashlet.id}`
  );

  const { calendarSources, plugins } = useContext(PluginContext);

  const queryClient = useQueryClient();

  const { t } = useTranslation();

  const [tooltipProps, tooltip] = useTooltip(
    {
      label: t('Authorize Calendars'),
      delay: ICON_BUTTON_DELAY,
    },
    () => !isMobile
  );

  const { data: userConfig, isLoading: userConfigLoading } = useQuery({
    queryKey: dashboardUserConfigKey,
    queryFn: () => fetchAndRepairDashboardConfig(dashboardPageConfigKey),
    refetchOnWindowFocus: false,
    enabled: Boolean(getExternalCalendarsFlagFromDashlet(dashlet)),
  });

  const configMutation = useMutation({
    mutationFn: (result) => {
      return TeamMemberService.setPageConfig(dashboardPageConfigKey, result);
    },
    onMutate: async (updated) => {
      await queryClient.cancelQueries({ queryKey: dashboardUserConfigKey });
      const previousData = queryClient.getQueryData(dashboardUserConfigKey);

      queryClient.setQueryData(dashboardUserConfigKey, updated);

      return { previousData };
    },
    onError: (_err, _updated, context) => {
      queryClient.setQueryData(dashboardUserConfigKey, context?.previousData);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: dashboardUserConfigKey });
    },
  });

  const handleChangeCalendarSelection = useCallback(
    (selection: any) => {
      const newConfig = {
        ...userConfig,
        calendarSelections: {
          ...userConfig?.calendarSelections,
          [dashlet.id]: selection,
        },
      };

      configMutation.mutate(newConfig);
    },
    [dashlet.id, userConfig, configMutation]
  );

  const { calendars, isLoading: calendarsLoading } = useCalendarOptions(
    true,
    calendarSources,
    plugins
  );

  const groupedOptions = useMemo(() => {
    const plugins = Object.keys(calendars);
    const optionsByUniqueSource: Record<
      string,
      { label: string; options: any[] }
    > = {};
    const totalOptions: any[] = [];

    plugins.forEach((plugin) => {
      const calendarsForPlugin = calendars[plugin];

      calendarsForPlugin.forEach((c) => {
        const key = `${c.source.plugin_api_name}-${c.source.api_name}`;
        if (!optionsByUniqueSource[key]) {
          optionsByUniqueSource[key] = {
            label: c.source.name,
            options: [],
          };
        }

        optionsByUniqueSource[key].options.push({
          label: c.calendar.name,
          value: encodeOptionValue(
            c.calendar.id,
            c.source.plugin_api_name,
            c.source.api_name
          ),
        });
      });
    });

    Object.keys(optionsByUniqueSource).forEach((key) => {
      if (!optionsByUniqueSource[key].options.length) {
        return;
      }

      const group = {
        id: key,
        label: optionsByUniqueSource[key].label,
        options: optionsByUniqueSource[key].options,
      };

      totalOptions.push(group);
    });

    return totalOptions;
  }, [calendars]);

  const optionCount = useMemo(() => {
    return groupedOptions.reduce(
      (acc: number, group: { options: any[] }) => acc + group.options.length,
      0
    );
  }, [groupedOptions]);

  const needsConnection =
    optionCount === 0 && errorServices.length > 0 && !isLoading;

  // The value selected by the user, or the default fallback
  const rawValue = useMemo(() => {
    if (!userConfig?.calendarSelections?.[dashlet.id]) {
      const flatCalendars = Object.values(calendars).flat();

      const values = flatCalendars
        .filter((c) => c.calendar?.default)
        .map((c) => ({
          label: c.calendar.name,
          value: encodeOptionValue(
            c.calendar.id,
            c.source.plugin_api_name,
            c.source.api_name
          ),
        }));

      return values;
    }

    return userConfig?.calendarSelections?.[dashlet.id];
  }, [userConfig, dashlet.id, calendars]);

  // The usable value with options that are currently disabled filtered out
  const value = useMemo(() => {
    const flatOptionsMap = groupedOptions.reduce((acc, curr) => {
      return {
        ...acc,
        ...curr.options.reduce(
          (optionAcc: any, option: any) => {
            optionAcc[option.value] = true;
            return optionAcc;
          },
          {} as Record<string, any>
        ),
      };
    }, {});

    return rawValue.filter((v: any) => flatOptionsMap[v.value]);
  }, [rawValue, groupedOptions]);

  if (!target || userConfigLoading || calendarsLoading) {
    return null;
  }

  return createPortal(
    <>
      {needsConnection ? (
        <Link
          to={`/marketplace/${errorServices[0]}/auth`}
          className="pt-1 flex items-center text-button-tertiary-default dashlet-control dashlet-calendar-menu pb-1"
          {...tooltipProps}
        >
          <Icon
            icon="action-connect-calendars"
            size="md"
            tooltipEnabled={false}
          />
          {tooltip}
        </Link>
      ) : (
        <IconDropdown
          icon="action-select-calendars"
          onChange={handleChangeCalendarSelection}
          value={value}
          isMobile={isMobile}
          options={groupedOptions}
          isLoading={calendarsLoading}
        />
      )}
    </>,
    target
  );
};
